import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TourService } from '../shared/tour/tour.service';
import { tourForUrl } from '../shared/tour/tour-definitions';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPause, faListAlt, faRightFromBracket, faGear, faStar, faHome, faRobot, faUser, faShieldHalved, faHandHoldingHeart, faGift, faChartLine, faCircleQuestion, faTrophy, faXmark, faCompass, faTableCellsLarge } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { AvatarTheme, resolveAvatarTheme } from '../shared/avatar/avatar.config';
import { resolveAvatarPresetPreview, resolveUserAvatarUrl } from '../shared/avatar/avatar-presets';
import { getUserLevel, UserLevel } from '../shared/level.utils';
import { IonicModule, MenuController } from '@ionic/angular';
import { UserProfileSyncService } from '../services/user-profile-sync.service';
import { AuthService } from '../services/auth.service';
import { FirestoreService } from '../services/firestore.service';
import { TranslateModule } from '@ngx-translate/core';
import { workTypeDisplay } from '../shared/work-type.utils';
import { GamificationService } from '../services/gamification.service';
import { RoleService } from '../services/role.service';
import { APP_VERSION } from '../../environments/version';

type MenuItem = {
  kind: 'link' | 'action';
  label: string;
  icon: IconProp;
  route?: string;
  action?: 'give-karma' | 'tour' | 'suite';
  exact?: boolean;
};

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, FontAwesomeModule, IonicModule, TranslateModule],
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css']
})
export class NavigationComponent implements OnInit {
  readonly appVersion = APP_VERSION;
  /** Otras apps de la suite con licencia para la empresa de quien usa la app (selector de apps). */
  suiteApps: Array<{ key: string; name: string; url: string }> = [];
  @Input() username: string = 'Usuario';
  @Input() workType: string | null = null;
  @Input() avatarKey: string | null = null;
  @Input() customAvatar: string | null = null;
  @Input() userPoints: number | null = null;
  @Output() logout = new EventEmitter<void>();

  /** Mascota y descripción corta de cada app de la suite (imágenes en assets/suite/). */
  readonly suiteMeta: Record<string, { tag: string; color: string }> = {
    safe: { tag: 'Seguridad de campo', color: '#fbbf24' },
    audit: { tag: 'Auditorías ISO', color: '#3b82f6' },
    talent: { tag: 'Gestión del talento', color: '#e879f9' }
  };
  readonly suiteFallback = { tag: 'Abrir app', color: '#94a3b8' };

  menuIsOpening = false;
  suiteOpen = false;
  readonly adminConsoleUrl = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:4201'
    : 'https://sb-admin.doctorelecktron.com';
  private readonly roleService = inject(RoleService);
  readonly isAdmin$ = this.roleService.hasAnyRole(['admin', 'superAdmin', 'support', 'moderator']);

  ngOnInit(): void {
    this.http
      .get<{ launcher?: Array<{ key: string; name: string; url: string }> }>(`${environment.apiUrl}/suite/me/apps`)
      .subscribe({
        next: (res) => (this.suiteApps = (res.launcher ?? []).filter((a) => a.key !== 'break')),
        error: () => (this.suiteApps = [])
      });
  }
  private readonly defaultAvatarUrl: string = resolveAvatarPresetPreview('avatar1') ?? 'https://api.dicebear.com/7.x/personas/svg?seed=avatar1';

  constructor(
    private readonly menuCtrl: MenuController,
    private readonly userProfileSync: UserProfileSyncService,
    private readonly authService: AuthService,
    private readonly firestoreService: FirestoreService,
    private readonly gamificationService: GamificationService,
    private readonly router: Router,
    private readonly tour: TourService,
    private readonly http: HttpClient

  ) {
    this.userProfileSync.event$.subscribe(event => {
      if (event?.type === 'profile-updated' && event.payload) {
        const payload = event.payload;
        if (Object.prototype.hasOwnProperty.call(payload, 'workType')) {
          this.workType = payload.workType ?? null;
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'avatar')) {
          this.avatarKey = payload.avatar ?? null;
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'customAvatar')) {
          this.customAvatar = payload.customAvatar ?? null;
        }
      }
    });
    this.authService.currentUser$.subscribe(user => {
      if (user && user.uid) {
        this.firestoreService.getUserProfile(user.uid).subscribe(profile => {
          if (profile) {
            this.workType = profile.workType;
            const rawAvatar = typeof profile.avatar === 'object' && profile.avatar !== null
              ? profile.avatar.id
              : (typeof profile.avatar === 'string' ? profile.avatar : null);
            const resolvedKey = profile.avatarPreset || rawAvatar;
            if (resolvedKey) {
              this.avatarKey = resolvedKey;
            }
            const resolvedCustom = profile.customAvatar || profile.photoURL || null;
            if (resolvedCustom) {
              this.customAvatar = resolvedCustom;
            }
            const shortName = this.buildShortNameFromProfile(profile);
            if (shortName) {
              this.username = shortName;
            } else if (profile.displayName) {
              this.username = profile.displayName;
            }
          }
        });
      }
    });
  }

  readonly menuItems: MenuItem[] = [
    {
      kind: 'link',
      label: 'MENU.HOME',
      route: '/dashboard/home',
      icon: faHome,
      exact: true
    },
    {
      kind: 'link',
      label: 'MENU.PAUSES_HUB',
      route: '/dashboard/centro-pausas',
      icon: faPause,
      exact: true
    },
    {
      kind: 'link',
      label: 'MENU.PROGRESS',
      route: '/dashboard/mi-progreso',
      icon: faChartLine,
      exact: true
    },
    {
      kind: 'link',
      label: 'MENU.RANKING',
      route: '/dashboard/ranking',
      icon: faTrophy,
      exact: true
    },
    {
      kind: 'link',
      label: 'MENU.COMMUNITY',
      route: '/dashboard/feed',
      icon: faListAlt,
      exact: true
    },
    {
      kind: 'link',
      label: 'MENU.STORE',
      route: '/dashboard/tienda',
      icon: faGift,
      exact: true
    },
    {
      kind: 'link',
      label: 'MENU.HELP',
      route: '/dashboard/ayuda',
      icon: faCircleQuestion,
      exact: true
    },
    {
      kind: 'action',
      label: 'MENU.TUTORIAL',
      icon: faCompass,
      action: 'tour'
    },
    {
      kind: 'action',
      label: 'SmartSuite',
      icon: faTableCellsLarge,
      action: 'suite'
    }
  ];

  public readonly faSignOutIcon: IconProp = faRightFromBracket;
  public readonly faGearIcon: IconProp = faGear;
  public readonly faStarIcon: IconProp = faStar;
  public readonly faShieldIcon: IconProp = faShieldHalved;
  public readonly faCloseIcon: IconProp = faXmark;

  get avatarTheme(): AvatarTheme {
    return resolveAvatarTheme(this.avatarKey) ?? { key: 'custom', label: 'Default Avatar', icon: faUser, color: '#000', background: '#fff' };
  }
  
  get avatarIcon(): IconProp {
    return this.avatarTheme.icon;
  }

  get avatarImageUrl(): string {
    return resolveUserAvatarUrl(this.avatarKey, this.customAvatar);
  }

  get userLevel(): UserLevel | null {
    if (this.userPoints === null) {
      return null;
    }
    return getUserLevel(this.userPoints);
  }

  get workTypeDisplay(): string | null {
    if (!this.workType) return null;
    const raw = this.workType.toString().trim().toLowerCase();
    if (raw === 'técnico' || raw === 'tecnico') return 'Técnico';
    if (raw === 'administrativo') return 'Administrativo';
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }

  onLogoutClick(): void {
    this.logout.emit();
    this.menuCtrl.close();
  }

  onMenuItemClick(): void {
    this.menuCtrl.close();
  }

  onActionClick(action: MenuItem['action']): void {
    if (action === 'give-karma') {
      this.gamificationService.openGiveKarma();
    }
    if (action === 'suite') {
      void this.menuCtrl.close().then(() => (this.suiteOpen = true));
      return;
    }
    if (action === 'tour') {
      // Tutorial de la pantalla actual; si no tiene uno propio, el tour general.
      const id = tourForUrl(this.router.url) ?? 'general';
      void this.menuCtrl.close().then(() => setTimeout(() => void this.tour.start(id), 250));
      return;
    }
    this.menuCtrl.close();
  }

  onMenuOpen(): void {
    this.menuIsOpening = true;
    setTimeout(() => {
      this.menuIsOpening = false;
    }, 1000);
  }

  private buildShortNameFromProfile(profile: any): string {
    const firstNames = this.normalizeNameSegment(profile?.firstNames);
    const lastNames = this.normalizeNameSegment(profile?.lastNames);

    if (firstNames) {
      const firstToken = firstNames.split(' ')[0];
      const lastToken = lastNames ? lastNames.split(' ')[0] : '';
      const composed = [firstToken, lastToken].filter(Boolean).join(' ').trim();
      if (composed) {
        return composed;
      }
    }

    const displayName = this.normalizeNameSegment(profile?.displayName);
    if (displayName) {
      const parts = displayName.split(' ').filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0]} ${parts[1]}`;
      }
      return parts[0];
    }

    return '';
  }

  private normalizeNameSegment(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim().replace(/\s+/g, ' ');
  }
}

